import store from "../store";
const color = "#fff";
import helpersJs from "./helpers";

export default {
  generatePdfHeader(title, company, client, themeColor = "#1976d2") {
    // Safe fallbacks for missing company fields
    const compName = company?.name || "Our Company";
    const compPhone = company?.primary_phone
      ? `Phone: ${company.primary_phone}`
      : "";
    const compEmail = company?.email ? `Email: ${company.email}` : "";
    const compTax = company?.vat_number
      ? `VAT No: ${company.vat_number}`
      : company?.reg_number
      ? `Reg No: ${company.reg_number}`
      : "";

    // Construct company address array
    const compAddress = [];
    if (company?.primary_address) compAddress.push(company.primary_address);
    if (company?.city) compAddress.push(company.city);
    if (company?.postal_code) compAddress.push(company.postal_code);
    const compAddressStr = compAddress.join(", ");

    // Safe fallbacks for missing client fields
    const clientName = client?.name || "Valued Client";
    const clientInstitution = client?.institution || "";
    const clientAddress = client?.address || "";
    const clientPosition = client?.position || "";
    const clientReference = client?.client_reference || "";
    const clientPostalCode = client?.postal_code || "";
    const clientPhone = client?.phone ? `Phone: ${client.phone}` : "";
    const clientEmail = client?.email ? `Email: ${client.email}` : "";

    return {
      stack: [
        // ROW 1: Company Meta & Document Title Banner Banner Area
        {
          columns: [
            // Left: Company Core Branding Block
            {
              width: "65%",
              stack: [
                {
                  // Structural left colored accent border line mimicking a modern letterhead logo
                  columns: [
                    {
                      canvas: [
                        {
                          type: "rect",
                          x: 0,
                          y: 0,
                          w: 4,
                          h: 22,
                          color: themeColor,
                        },
                      ],
                    },
                    {
                      text: compName.toUpperCase(),
                      fontSize: 16,
                      bold: true,
                      color: "#212121",
                      margin: [-12, 1, 0, 0], // Align next to accent bar
                    },
                  ],
                },
                {
                  text: compAddressStr,
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 6, 0, 2],
                },
                {
                  text: [
                    compPhone ? `${compPhone}   |   ` : "",
                    compEmail ? `${compEmail}` : "",
                  ],
                  fontSize: 9,
                  color: "#616161",
                },
                compTax
                  ? {
                      text: compTax,
                      fontSize: 8,
                      color: "#757575",
                      margin: [0, 2, 0, 0],
                    }
                  : {},
              ],
            },
            // Right: Document Big Title & Date
            {
              width: "35%",
              alignment: "right",
              stack: [
                {
                  text: title.toUpperCase(),
                  fontSize: 20,
                  bold: true,
                  color: themeColor,
                  letterSpacing: 1,
                },
                {
                  text: [
                    { text: "Date: ", bold: true, color: "#424242" },
                    {
                      text: new Date().toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }),
                    },
                  ],
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 6, 0, 0],
                },
              ],
            },
          ],
        },

        // Elegant Minimal Spacer Divider
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 0.75,
              lineColor: "#e0e0e0",
            },
          ],
          margin: [0, 15, 0, 15],
        },

        // ROW 2: "Recipient Details" Metadata Panel
        {
          columns: [
            {
              width: "100%",
              stack: [
                {
                  text: "PREPARED FOR:",
                  fontSize: 8,
                  bold: true,
                  color: themeColor,
                  letterSpacing: 0.5,
                  margin: [0, 0, 0, 4],
                },
                {
                  text: clientName,
                  fontSize: 11,
                  bold: true,
                  color: "#212121",
                },
                clientInstitution
                  ? {
                      text: clientInstitution,
                      fontSize: 9,
                      color: "#424242",
                      italics: true,
                      margin: [0, 1, 0, 1],
                    }
                  : {},
                clientPosition
                  ? {
                      text: `[${clientPosition}]`,
                      fontSize: 9,
                      color: "#424242",
                      italics: true,
                      margin: [0, 1, 0, 1],
                    }
                  : {},
                clientAddress
                  ? {
                      text: clientAddress,
                      fontSize: 9,
                      color: "#424242",
                      italics: true,
                      margin: [0, 1, 0, 1],
                    }
                  : {},
                clientPostalCode
                  ? {
                      text: clientPostalCode,
                      fontSize: 9,
                      color: "#424242",
                      italics: true,
                      margin: [0, 1, 0, 1],
                    }
                  : {},
                clientReference
                  ? {
                      text: `Client Ref #: ${clientReference}`,
                      fontSize: 9,
                      color: "#424242",
                      italics: true,
                      margin: [0, 1, 0, 1],
                    }
                  : {},
                {
                  text: [
                    clientPhone ? `${clientPhone}` : "",
                    clientPhone && clientEmail ? "   •   " : "",
                    clientEmail ? `${clientEmail}` : "",
                  ],
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 2, 0, 0],
                },
              ],
            },
          ],
        },

        // Final spacing boundary before specific page transaction body contents load
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 0.5,
              lineColor: "#e0e0e0",
            },
          ],
          margin: [0, 12, 0, 15],
        },
      ],
    };
  },
  paymentsStatementSubHeader(client, themeColor) {
    // Safe fallbacks for missing client fields
    const clientName = client?.name || "Valued Client";
    const clientInstitution = client?.institution || "";
    const clientAddress = client?.address || "";
    const clientPosition = client?.position || "";
    const clientReference = client?.client_reference || "";
    const clientPostalCode = client?.postal_code || "";
    const clientPhone = client?.phone || "";
    const clientEmail = client?.email || "";

    // Helper function for consistent key-value metadata rows
    const renderField = (label, value) => {
      if (!value) return null;
      return {
        text: [
          { text: `${label}: `, bold: true, color: "#424242" },
          { text: String(value), color: "#616161" },
        ],
        fontSize: 9,
        margin: [0, 2, 0, 2],
      };
    };

    // Build left & right column stacks, filtering out null/empty items
    const leftColumnDetails = [
      renderField("Client Name", clientName),
      renderField("Institution", clientInstitution),
      renderField("Position", clientPosition),
      renderField("Reference #", clientReference),
    ].filter(Boolean);

    const rightColumnDetails = [
      renderField("Address", clientAddress),
      renderField("Postal Code", clientPostalCode),
      renderField("Phone", clientPhone),
      renderField("Email", clientEmail),
    ].filter(Boolean);

    return {
      stack: [
        // Top Header Row: Date Alignment

        // Section Header
        {
          text: "PREPARED FOR",
          fontSize: 8,
          bold: true,
          color: themeColor || "#1976D2",
          characterSpacing: 0.8,
          margin: [0, 0, 0, 6],
        },

        // Two-Column Client Details Layout
        {
          columns: [
            // Left Column
            {
              width: "65%",
              stack: leftColumnDetails,
            },
            // Right Column
            {
              width: "35%",
              stack: rightColumnDetails,
            },
          ],
          columnGap: 20,
        },
      ],
      margin: [0, 0, 0, 5], // Bottom margin for spacing before next document section
    };
  },

  generatePdfHeaderWithoutClientDetailsAndNoLogo(
    title,
    company,
    client,
    themeColor = "#1976d2",
    date = null
  ) {
    // Safe fallbacks for missing company fields
    const compName = company?.name || "Our Company";
    const compPhone = company?.primary_phone
      ? `Phone: ${company.primary_phone}`
      : "";
    const compEmail = company?.email ? `Email: ${company.email}` : "";
    const compTax = company?.vat_number
      ? `VAT No: ${company.vat_number}`
      : company?.reg_number
      ? `Reg No: ${company.reg_number}`
      : "";

    // Construct company address array
    const compAddress = [];
    if (company?.primary_address) compAddress.push(company.primary_address);
    if (company?.city) compAddress.push(company.city);
    if (company?.postal_code) compAddress.push(company.postal_code);
    const compAddressStr = compAddress.join(", ");

    return {
      stack: [
        // ROW 1: Company Meta & Document Title Banner Banner Area
        {
          columns: [
            // Left: Company Core Branding Block
            {
              width: "65%",
              stack: [
                {
                  // Structural left colored accent border line mimicking a modern letterhead logo
                  columns: [
                    {
                      canvas: [
                        {
                          type: "rect",
                          x: 0,
                          y: 0,
                          w: 4,
                          h: 22,
                          color: themeColor,
                        },
                      ],
                    },
                    {
                      text: compName.toUpperCase(),
                      fontSize: 16,
                      bold: true,
                      color: themeColor,
                      margin: [-12, 1, 0, 0], // Align next to accent bar
                    },
                  ],
                },
                {
                  text: compAddressStr,
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 6, 0, 2],
                },
                {
                  text: [
                    compPhone ? `${compPhone}   |   ` : "",
                    compEmail ? `${compEmail}` : "",
                  ],
                  fontSize: 9,
                  color: "#616161",
                },
                compTax
                  ? {
                      text: compTax,
                      fontSize: 8,
                      color: "#757575",
                      margin: [0, 2, 0, 0],
                    }
                  : {},
              ],
            },
            // Right: Document Big Title & Date
            {
              width: "35%",
              alignment: "right",
              stack: [
                {
                  text: title.toUpperCase(),
                  fontSize: 20,
                  bold: true,
                  color: themeColor,
                  letterSpacing: 1,
                },
                {
                  text: [
                    { text: "Date: ", bold: true, color: "#424242" },
                    {
                      text: `${
                        date
                          ? new Date(date).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : new Date().toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                      }`,
                    },
                  ],
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 6, 0, 0],
                },
              ],
            },
          ],
        },
      ],
    };
  },
  async generatePdfHeaderWithoutClientDetailsT1(
    title,
    company,
    client,
    themeColor = "#1976d2",
    date = null
  ) {
    // 1. Safely fetch logo Base64 string
    let logoBase64 = null;
    try {
      const logoUrl = store.getters.getLogo;
      if (logoUrl) {
        logoBase64 = await this.getBase64FromUrl(logoUrl);
      }
    } catch (e) {
      console.warn("Logo could not be loaded for PDF header:", e);
    }

    // Safe fallbacks for missing company fields
    const compName = company?.name || "Our Company";
    const compPhone = company?.primary_phone
      ? `Phone: ${company.primary_phone}`
      : "";
    const compEmail = company?.email ? `Email: ${company.email}` : "";
    const compTax = company?.vat_number
      ? `VAT No: ${company.vat_number}`
      : company?.reg_number
      ? `Reg No: ${company.reg_number}`
      : "";

    // Construct company address array
    const compAddress = [];
    if (company?.primary_address) compAddress.push(company.primary_address);
    if (company?.city) compAddress.push(company.city);
    if (company?.postal_code) compAddress.push(company.postal_code);
    const compAddressStr = compAddress.join(", ");

    // Format Date safely
    const formattedDate = date
      ? new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date().toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

    // 2. Build Company Title/Logo element
    const companyTitleBlock = logoBase64
      ? {
          image: logoBase64,
          fit: [140, 45], // Professionally constrained size maintaining aspect ratio
          margin: [0, 0, 0, 6],
        }
      : {
          // Fallback: Accent line + Company Name text if no logo
          columns: [
            {
              canvas: [
                { type: "rect", x: 0, y: 0, w: 4, h: 22, color: themeColor },
              ],
              width: 10,
            },
            {
              text: compName.toUpperCase(),
              fontSize: 16,
              bold: true,
              color: themeColor,
              margin: [0, 1, 0, 0],
            },
          ],
        };

    return {
      stack: [
        {
          columns: [
            // Left: Company Core Branding Block
            {
              width: "60%",
              stack: [
                companyTitleBlock,
                {
                  text: compAddressStr,
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 4, 0, 2],
                },
                {
                  text: [
                    compPhone ? `${compPhone}   |   ` : "",
                    compEmail ? `${compEmail}` : "",
                  ],
                  fontSize: 9,
                  color: "#616161",
                },
                compTax
                  ? {
                      text: compTax,
                      fontSize: 8,
                      color: "#757575",
                      margin: [0, 2, 0, 0],
                    }
                  : {},
              ],
            },
            // Right: Document Big Title & Date
            {
              width: "40%",
              alignment: "right",
              stack: [
                {
                  text: title.toUpperCase(),
                  fontSize: 20,
                  bold: true,
                  color: themeColor,
                  letterSpacing: 1,
                },
                {
                  text: [
                    { text: "Date: ", bold: true, color: "#424242" },
                    { text: formattedDate },
                  ],
                  fontSize: 9,
                  color: "#616161",
                  margin: [0, 6, 0, 0],
                },
              ],
            },
          ],
        },
      ],
    };
  },
  async generatePdfHeaderWithoutClientDetails(
    title,
    company,
    client,
    themeColor = "#1976d2",
    date = null
  ) {
    // 1. Safely fetch logo Base64 string
    let logoBase64 = null;
    try {
      const logoUrl = store.getters.getLogo;
      if (logoUrl) {
        //logoBase64 = await this.getBase64FromUrl(logoUrl);
        logoBase64 = await this.getRoundedBase64Logo(logoUrl, 40);
      }
    } catch (e) {
      console.warn("Logo could not be loaded for PDF header:", e);
    }

    // Safe fallbacks for missing company fields
    const compName = company?.name || "Our Company";
    const compPhone = company?.primary_phone
      ? `Phone: ${company.primary_phone}`
      : "";
    const compEmail = company?.email ? `Email: ${company.email}` : "";
    const compTax = company?.vat_number
      ? `VAT No: ${company.vat_number}`
      : company?.reg_number
      ? `Reg No: ${company.reg_number}`
      : "";

    // Construct company address array
    const compAddress = [];
    if (company?.primary_address) compAddress.push(company.primary_address);
    if (company?.city) compAddress.push(company.city);
    if (company?.postal_code) compAddress.push(company.postal_code);
    const compAddressStr = compAddress.join(", ");

    // Format Date safely
    const formattedDate = date
      ? new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date().toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

    // 2. Build Left Brand Element (Logo or Fallback Accent Box)
    const leftBrandBlock = logoBase64
      ? {
          stack: [
            {
              image: logoBase64,
              fit: [130, 50],
            },
          ],
        }
      : {
          // Text fallback if no logo exists
          columns: [
            {
              canvas: [
                { type: "rect", x: 0, y: 0, w: 4, h: 22, color: themeColor },
              ],
              width: 10,
            },
            {
              text: compName.toUpperCase(),
              fontSize: 16,
              bold: true,
              color: themeColor,
            },
          ],
        };

    return {
      stack: [
        // ROW 1: Logo on Left, Full Company Details on Right
        {
          columns: [
            // Left: Logo
            {
              width: "40%",
              stack: [leftBrandBlock],
            },
            // Right: Company Name & Address Stack
            {
              width: "60%",
              alignment: "right",
              stack: [
                {
                  text: compName.toUpperCase(),
                  fontSize: 14,
                  bold: true,
                  color: themeColor,
                  margin: [0, 0, 0, 2],
                },
                {
                  text: compAddressStr,
                  fontSize: 8.5,
                  color: "#616161",
                  margin: [0, 0, 0, 2],
                },
                {
                  text: [
                    compPhone ? `${compPhone}   |   ` : "",
                    compEmail ? `${compEmail}` : "",
                  ],
                  fontSize: 8.5,
                  color: "#616161",
                },
                compTax
                  ? {
                      text: compTax,
                      fontSize: 8,
                      color: "#757575",
                      margin: [0, 2, 0, 0],
                    }
                  : {},
                {
                  text: [
                    { text: "Date: ", bold: true, color: "#424242" },
                    { text: formattedDate },
                  ],
                  fontSize: 8.5,
                  color: "#616161",
                  margin: [0, 3, 0, 0],
                },
              ],
            },
          ],
        },

        // Divider Line
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 10,
              x2: 515,
              y2: 10,
              lineWidth: 0.5,
              lineColor: "#E2E8F0",
            },
          ],
        },

        // ROW 2: Document Title Centered Immediately Below
        {
          text: title.toUpperCase(),
          fontSize: 18,
          bold: true,
          color: themeColor,
          alignment: "center",
          characterSpacing: 1.5,
          margin: [0, 10, 0, 5],
        },
      ],
    };
  },
  async generatePdfHeaderCompact(
    title,
    company,
    client,
    themeColor = "#1976d2",
    date = null
  ) {
    // 1. Fetch rounded Base64 logo
    let logoBase64 = null;
    try {
      const logoUrl = store.getters.getLogo;
      if (logoUrl) {
        logoBase64 = await this.getRoundedBase64Logo(logoUrl, 20);
      }
    } catch (e) {
      console.warn("Logo could not be loaded for PDF header:", e);
    }

    // Safe fallbacks for company fields
    const compName = company?.name || "Our Company";
    const compPhone = company?.primary_phone
      ? `Phone: ${company.primary_phone}`
      : "";
    const compEmail = company?.email ? `Email: ${company.email}` : "";
    const compTax = company?.vat_number
      ? `VAT No: ${company.vat_number}`
      : company?.reg_number
      ? `Reg No: ${company.reg_number}`
      : "";

    // Construct company address array
    const compAddress = [];
    if (company?.primary_address) compAddress.push(company.primary_address);
    if (company?.city) compAddress.push(company.city);
    if (company?.postal_code) compAddress.push(company.postal_code);
    const compAddressStr = compAddress.join(", ");

    // Format Date safely
    const formattedDate = date
      ? new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date().toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

    // 2. Build Logo + Company Name horizontal layout
    const logoAndNameBlock = logoBase64
      ? {
          columns: [
            {
              image: logoBase64,
              fit: [50, 50],
              width: 54,
            },
            {
              text: compName.toUpperCase(),
              fontSize: 18, // Bigger company name
              bold: true,
              color: themeColor, // Colored with primary theme
              margin: [8, 10, 0, 0], // Vertically centers text next to logo
            },
          ],
        }
      : {
          text: compName.toUpperCase(),
          fontSize: 18, // Bigger company name
          bold: true,
          color: themeColor, // Colored with primary theme
        };

    return {
      stack: [
        // ==========================================
        // ROW 1: [Logo + Big Colored Company Name] (Left)  |  [Big Colored Title] (Right)
        // ==========================================
        {
          columns: [
            // Left Side: Logo + Name
            {
              width: "65%",
              stack: [logoAndNameBlock],
            },
            // Right Side: Document Title & Date
            {
              width: "35%",
              alignment: "right",
              stack: [
                {
                  text: title.toUpperCase(),
                  fontSize: 20,
                  bold: true,
                  color: themeColor,
                  characterSpacing: 1,
                },
                {
                  text: [
                    { text: "Date: ", bold: true, color: "#424242" },
                    { text: formattedDate },
                  ],
                  fontSize: 8.5,
                  color: "#616161",
                  margin: [0, 4, 0, 0],
                },
              ],
            },
          ],
        },

        // ==========================================
        // ROW 2: Company Address Details (Left side, directly under logo/name)
        // ==========================================
        {
          margin: [0, 8, 0, 0],
          columns: [
            {
              width: "65%",
              stack: [
                compAddressStr
                  ? {
                      text: compAddressStr,
                      fontSize: 8.5,
                      color: "#616161",
                      margin: [0, 0, 0, 2],
                    }
                  : {},
                {
                  text: [
                    compPhone ? `${compPhone}   |   ` : "",
                    compEmail ? `${compEmail}` : "",
                  ],
                  fontSize: 8.5,
                  color: "#616161",
                },
                compTax
                  ? {
                      text: compTax,
                      fontSize: 8,
                      color: "#757575",
                      margin: [0, 2, 0, 0],
                    }
                  : {},
              ],
            },
            {
              text: "", // Valid PDFMake text element placeholder
              width: "35%",
            },
          ],
        },

        // Divider Line
        {
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 12,
              x2: 515,
              y2: 12,
              lineWidth: 0.5,
              lineColor: "#E2E8F0",
            },
          ],
        },
      ],
    };
  },
  generateClientSubHeaderAuto(title, data) {
    // Structured Matter Metadata Box
    const itsLegal = store.getters.getAppMode === "legal" ? true : false;
    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Client Name:", style: "metaLabel" },
            {
              text: data?.clientName || data?.client?.name || "",
              style: "metaValue",
            },
            {
              text: `${
                title.toLowerCase().includes("invoice")
                  ? "Invoice"
                  : "Quotation"
              } Number:`,
              style: "metaLabel",
            },
            {
              text:
                data?.invoice_no ||
                data?.invoice_number ||
                data?.quote_number ||
                "",
              style: "metaValueHighlight",
            },
          ],
          [
            { text: "Client Ref:", style: "metaLabel" },
            {
              text:
                data?.clientRef ||
                data?.client?.client_reference ||
                data?.client_reference ||
                "",
              style: "metaValue",
            },
            {
              text: itsLegal ? "Internal Ref:" : "Address:",
              style: "metaLabel",
            },
            {
              text: itsLegal
                ? data?.internalRef || "N/A"
                : `${data?.client?.address || ""}, ${
                    data?.client?.city || ""
                  }` || "",
              style: "metaValue",
            },
          ],
          [
            { text: "Car Make:", style: "metaLabel" },
            { text: data?.make || "", style: "metaValue" },
            { text: "Licence Plate:", style: "metaLabel" },
            { text: data?.license_plate || "", style: "metaValue" },
          ],
          [
            { text: "Car Model:", style: "metaLabel" },
            { text: data?.model || "", style: "metaValue" },
            { text: "Year & VIN:", style: "metaLabel" },
            {
              text: `${data?.year || ""} | ${data?.vin || ""}` || "",
              style: "metaValue",
            },
          ],
        ],
      },
      layout: {
        hLineWidth: function () {
          return 0.5;
        },
        vLineWidth: function () {
          return 0;
        },
        hLineColor: function () {
          return "#E2E8F0";
        },
        paddingLeft: function () {
          return 6;
        },
        paddingRight: function () {
          return 6;
        },
        paddingTop: function () {
          return 4;
        },
        paddingBottom: function () {
          return 4;
        },
      },
    };
  },
  generateClientSubHeaderCase(title, data) {
    const isInvoice = title.toLowerCase().includes("invoice");

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Client Name:", style: "metaLabel" },
            {
              text: data?.clientName || data?.client?.name || "",
              style: "metaValue",
            },
            {
              text: `${isInvoice ? "Invoice" : "Quotation"} Number:`,
              style: "metaLabel",
            },
            {
              text:
                data?.invoice_no ||
                data?.invoice_number ||
                data?.quote_number ||
                "",
              style: "metaValueHighlight",
            },
          ],
          [
            { text: "Client Ref:", style: "metaLabel" },
            {
              text:
                data?.clientRef ||
                data?.client?.client_reference ||
                data?.client_reference ||
                "",
              style: "metaValue",
            },
            { text: "Internal Ref:", style: "metaLabel" },
            { text: data?.internalRef || "N/A", style: "metaValue" },
          ],
          [
            { text: "Court Case No:", style: "metaLabel" },
            { text: data?.courtCaseNo || "N/A", style: "metaValue" },
            { text: "SAPS Case No:", style: "metaLabel" },
            { text: data?.sapsCaseNo || "N/A", style: "metaValue" },
          ],
          [
            { text: "Seat of Court:", style: "metaLabel" },
            { text: data?.seat_of_court || "N/A", style: "metaValue" },
            { text: "Instruction Date:", style: "metaLabel" },
            { text: data?.instruction_date || "N/A", style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  generateClientSubHeaderProject(title, data) {
    const isInvoice = title.toLowerCase().includes("invoice");

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Client Name:", style: "metaLabel" },
            {
              text: data?.clientName || data?.client?.name || "",
              style: "metaValue",
            },
            {
              text: `${isInvoice ? "Invoice" : "Quotation"} Number:`,
              style: "metaLabel",
            },
            {
              text:
                data?.invoice_no ||
                data?.invoice_number ||
                data?.quote_number ||
                "",
              style: "metaValueHighlight",
            },
          ],
          [
            { text: "Client Ref:", style: "metaLabel" },
            {
              text:
                data?.clientRef ||
                data?.client_reference ||
                data?.client?.client_reference ||
                data?.client_reference ||
                "",
              style: "metaValue",
            },
            { text: "Project Title:", style: "metaLabel" },
            { text: data?.title || "", style: "metaValue" },
          ],
          [
            { text: "Project Ref:", style: "metaLabel" },
            { text: data?.reference_number || "N/A", style: "metaValue" },
            { text: "Location:", style: "metaLabel" },
            { text: data?.location || "", style: "metaValue" },
          ],
          [
            { text: "Start Date:", style: "metaLabel" },
            { text: data?.start_date || "N/A", style: "metaValue" },
            { text: "Due Date:", style: "metaLabel" },
            { text: data?.due_date || "N/A", style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  generateClientSubHeaderEvent(title, data) {
    const isInvoice = title.toLowerCase().includes("invoice");

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Client Name:", style: "metaLabel" },
            {
              text: data?.clientName || data?.client?.name || "",
              style: "metaValue",
            },
            {
              text: `${isInvoice ? "Invoice" : "Quotation"} Number:`,
              style: "metaLabel",
            },
            {
              text:
                data?.invoice_no ||
                data?.invoice_number ||
                data?.quote_number ||
                "",
              style: "metaValueHighlight",
            },
          ],
          [
            { text: "Client Ref:", style: "metaLabel" },
            {
              text:
                data?.clientRef ||
                data?.client_reference ||
                data?.client?.client_reference ||
                data?.client_reference ||
                "",
              style: "metaValue",
            },
            { text: "Event Title:", style: "metaLabel" },
            { text: data?.title || "", style: "metaValue" },
          ],
          [
            { text: "Event Ref:", style: "metaLabel" },
            { text: data?.reference_number || "N/A", style: "metaValue" },
            { text: "Venue:", style: "metaLabel" },
            { text: data?.location || "", style: "metaValue" },
          ],
          [
            { text: "Event Date:", style: "metaLabel" },
            { text: data?.start_date || "N/A", style: "metaValue" },
            { text: "End / Due Date:", style: "metaLabel" },
            { text: data?.due_date || "N/A", style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  generatePrivatePracticeSubHeader(title, data) {
    const doc = data || {};
    const practitioner = doc.practitioner || {};
    const client = doc.client || {};
    const isInvoice = title && title.toLowerCase().indexOf("invoice") !== -1;

    // Practitioner Details
    const practitionerName = practitioner.name || "";
    const practiceNo = practitioner.practice_number || "";
    const hpcsaNo = practitioner.hpcsa_number || "";
    const vatNo = practitioner.vat_number || "Non-VAT Vendor";

    // Patient & Billing Details
    const clientName = doc.clientName || doc.client_name || client.name || "";
    const clientRef =
      doc.clientRef || doc.client_reference || client.client_reference || "";
    const invoiceNo = doc.invoice_number || doc.invoice_no || "";
    const fileRef = doc.reference_number || "N/A";
    const icd10 = doc.icd10_code || "N/A";
    const medicalAidName = doc.medical_aid_name || "Private / Self-Pay";
    const medicalAidNo = doc.medical_aid_number || "N/A";
    let invoiceDate = doc.invoice_date || doc.created_at || "N/A";
    invoiceDate = helpersJs.humanFriendlyDate(invoiceDate);
    const dueDate = doc.due_date || "";

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Practitioner:", style: "metaLabel" },
            { text: practitionerName, style: "metaValue" },
            {
              text: (isInvoice ? "Invoice" : "Quotation") + " Number:",
              style: "metaLabel",
            },
            { text: invoiceNo, style: "metaValueHighlight" },
          ],
          [
            { text: "Practice / HPCSA:", style: "metaLabel" },
            {
              text: practiceNo + (hpcsaNo ? " / " + hpcsaNo : ""),
              style: "metaValue",
            },
            { text: "Invoice Date:", style: "metaLabel" },
            { text: invoiceDate, style: "metaValue" },
          ],
          [
            { text: "Patient Name:", style: "metaLabel" },
            { text: clientName, style: "metaValue" },
            { text: "Payment Due:", style: "metaLabel" },
            { text: dueDate, style: "metaValue" },
          ],
          [
            { text: "Patient Ref / ID:", style: "metaLabel" },
            { text: clientRef, style: "metaValue" },
            { text: "File Ref:", style: "metaLabel" },
            { text: fileRef, style: "metaValue" },
          ],
          [
            { text: "Medical Scheme:", style: "metaLabel" },
            { text: medicalAidName, style: "metaValue" },
            { text: "Medical Aid No:", style: "metaLabel" },
            { text: medicalAidNo, style: "metaValue" },
          ],
          [
            { text: "ICD-10 Code:", style: "metaLabel" },
            { text: icd10, style: "metaValueHighlight" },
            { text: "VAT Reg No:", style: "metaLabel" },
            { text: vatNo, style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  generateSocialWorkSubHeader(title, data) {
    const doc = data || {};
    const practitioner = doc.practitioner || {};
    const client = doc.client || {};
    const isInvoice = title && title.toLowerCase().indexOf("invoice") !== -1;

    // Practitioner Details
    const practitionerName = practitioner.name || "";
    const sacsspNo = practitioner.sacssp_number || "";
    const practiceNo = practitioner.practice_number || "";
    const vatNo = practitioner.vat_number || "Non-VAT Vendor";

    // Client & Casework Details
    const clientName = doc.clientName || doc.client_name || client.name || "";
    const clientRef =
      doc.clientRef || doc.client_reference || client.client_reference || "";
    const invoiceNo = doc.invoice_number || doc.invoice_no || "";
    const fileRef = doc.reference_number || "N/A";
    const caseType = doc.case_type || "N/A";
    const referralSource = doc.referral_source || "N/A";
    const confidentiality = doc.confidentiality_level || "Standard";
    let invoiceDate = doc.invoice_date || doc.created_at || "N/A";
    invoiceDate = helpersJs.humanFriendlyDate(invoiceDate);
    const dueDate = doc.due_date || "";

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Social Worker:", style: "metaLabel" },
            { text: practitionerName, style: "metaValue" },
            {
              text: (isInvoice ? "Invoice" : "Quotation") + " Number:",
              style: "metaLabel",
            },
            { text: invoiceNo, style: "metaValueHighlight" },
          ],
          [
            { text: "SACSSP / BHF:", style: "metaLabel" },
            {
              text: sacsspNo + (practiceNo ? " / " + practiceNo : ""),
              style: "metaValue",
            },
            { text: "Invoice Date:", style: "metaLabel" },
            { text: invoiceDate, style: "metaValue" },
          ],
          [
            { text: "Client Name:", style: "metaLabel" },
            { text: clientName, style: "metaValue" },
            { text: "Payment Due:", style: "metaLabel" },
            { text: dueDate, style: "metaValue" },
          ],
          [
            { text: "Client Ref / ID:", style: "metaLabel" },
            { text: clientRef, style: "metaValue" },
            { text: "Case File Ref:", style: "metaLabel" },
            { text: fileRef, style: "metaValue" },
          ],
          [
            { text: "Case Type:", style: "metaLabel" },
            { text: caseType, style: "metaValue" },
            { text: "Referral Source:", style: "metaLabel" },
            { text: referralSource, style: "metaValue" },
          ],
          [
            { text: "Confidentiality:", style: "metaLabel" },
            {
              text: confidentiality.toUpperCase(),
              style: "metaValueHighlight",
            },
            { text: "VAT Reg No:", style: "metaLabel" },
            { text: vatNo, style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  generateClientSubHeaderGeneral(title, data) {
    const isInvoice = title.toLowerCase().includes("invoice");
    const addressParts = [data?.client?.address, data?.client?.city]
      .filter(Boolean)
      .join(", ");

    return {
      style: "metadataCard",
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            { text: "Client Name:", style: "metaLabel" },
            {
              text: data?.clientName || data?.client?.name || "",
              style: "metaValue",
            },
            {
              text: `${isInvoice ? "Invoice" : "Quotation"} Number:`,
              style: "metaLabel",
            },
            {
              text:
                data?.invoice_no ||
                data?.invoice_number ||
                data?.quote_number ||
                "",
              style: "metaValueHighlight",
            },
          ],
          [
            { text: "Client Ref:", style: "metaLabel" },
            {
              text:
                data?.clientRef ||
                data?.client?.client_reference ||
                data?.client_reference ||
                "",
              style: "metaValue",
            },
            { text: "Address:", style: "metaLabel" },
            { text: addressParts || "", style: "metaValue" },
          ],
          [
            { text: "Entity:", style: "metaLabel" },
            { text: data?.client?.institution || "", style: "metaValue" },
            { text: "Phone:", style: "metaLabel" },
            { text: data?.client?.phone || "", style: "metaValue" },
          ],
          [
            { text: "Position:", style: "metaLabel" },
            { text: data?.client?.position || "", style: "metaValue" },
            {
              text: `${isInvoice ? "Invoice" : "Quotation"} Date:`,
              style: "metaLabel",
            },
            { text: data?.invoice_date || "", style: "metaValue" },
          ],
        ],
      },
      layout: this.getMetadataTableLayout(),
    };
  },
  getMetadataTableLayout() {
    return {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => "#E2E8F0",
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 4,
      paddingBottom: () => 4,
    };
  },
  getBase64FromUrl(url) {
    return new Promise((resolve, reject) => {
      var img = new Image();
      img.setAttribute("crossOrigin", "anonymous");

      img.onload = () => {
        var canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const urlParts = url.split(".");
        const ext = urlParts[urlParts.length - 1];
        var dataURL = canvas.toDataURL(`image/${ext}`);

        resolve(dataURL);
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = url;
    });
  },
  async getRoundedBase64Logo(url, radius = 20) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Draw rounded path mask
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.arcTo(img.width, 0, img.width, img.height, radius);
        ctx.arcTo(img.width, img.height, 0, img.height, radius);
        ctx.arcTo(0, img.height, 0, 0, radius);
        ctx.arcTo(0, 0, img.width, 0, radius);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  },
  async receiptHeader() {
    return [
      {
        columns: [
          {
            image: await this.getBase64FromUrl(store.getters.getLogo),

            width: 150,
          },
          [
            {
              text: "Receipt",
              color: "#333333",
              width: "*",
              fontSize: 28,
              bold: true,
              alignment: "right",
              margin: [0, 0, 0, 15],
            },
            {
              stack: [
                {
                  columns: [
                    {
                      text: "Receipt No.",
                      color: "#aaaaab",
                      bold: true,
                      width: "*",
                      fontSize: 12,
                      alignment: "right",
                    },
                    {
                      text: "00001",
                      bold: true,
                      color: "#333333",
                      fontSize: 12,
                      alignment: "right",
                      width: 100,
                    },
                  ],
                },
                {
                  columns: [
                    {
                      text: "Date Issued",
                      color: "#aaaaab",
                      bold: true,
                      width: "*",
                      fontSize: 12,
                      alignment: "right",
                    },
                    {
                      text: "June 01, 2016",
                      bold: true,
                      color: "#333333",
                      fontSize: 12,
                      alignment: "right",
                      width: 100,
                    },
                  ],
                },
                {
                  columns: [
                    {
                      text: "Status",
                      color: "#aaaaab",
                      bold: true,
                      fontSize: 12,
                      alignment: "right",
                      width: "*",
                    },
                    {
                      text: "PAID",
                      bold: true,
                      fontSize: 14,
                      alignment: "right",
                      color: "green",
                      width: 100,
                    },
                  ],
                },
              ],
            },
          ],
        ],
      },
    ];
  },
  async companyHeader(textTitle) {
    let companyName = "";
    if (store.getters.getUserCompany.emis == "turning-heads-2025") {
      companyName = "Gence & Associates T/A Turning Heads";
    } else {
      companyName = store.getters.getUserCompany.name;
    }
    return [
      {
        style: [this.getPdfStyles().tableMainHeaderStyle],
        table: {
          widths: ["60%", "40%"],
          body: [
            [
              {
                text: textTitle,
                style: [this.getPdfStyles().header],
                alignment: "left",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: companyName,
                style: [this.getPdfStyles().subHeader],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                image: await this.getBase64FromUrl(store.getters.getLogo),
                border: [false, false, false, false],
                width: 75,
              },
              {
                text: store.getters.getUserCompany.primary_address,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: `VAT No: ${store.getters.getUserCompany.vat_number}`,
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: `${store.getters.getUserCompany.city}, ${store.getters.getUserCompany.postal_code}`,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: `REG No: ${store.getters.getUserCompany.reg_number}`,
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: store.getters.getUserCompany.primary_phone,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: " ",
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: store.getters.getUserCompany.email,
                style: [this.getPdfStyles().email, this.getPdfStyles().italics],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
          ],
        },
      },
      this.myCustomMarginHeaderBottom(),
    ];
  },
  async companyHeaderFrontPage(company, textTitle, logoBase64Url) {
    let companyName = "";
    if (company.emis == "turning-heads-2025") {
      companyName = "Gence & Associates T/A Turning Heads";
    } else {
      companyName = company.name;
    }
    return [
      {
        style: [this.getPdfStyles().tableMainHeaderStyle],
        table: {
          widths: ["60%", "40%"],
          body: [
            [
              {
                text: textTitle,
                style: [this.getPdfStyles().header],
                alignment: "left",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: companyName,
                style: [this.getPdfStyles().subHeader],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                image: logoBase64Url
                  ? logoBase64Url
                  : await this.getBase64FromUrl(store.getters.getLogo),
                border: [false, false, false, false],
                width: 75,
              },
              {
                text: company.primary_address,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: `VAT No: ${company.vat_number}`,
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: `${company.city}, ${company.postal_code}`,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: `REG No: ${company.reg_number}`,
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: company.primary_phone,
                style: [this.getPdfStyles().addressSection],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
            [
              {
                border: [false, false, false, false],
                text: " ",
                color,
                fillColor: store.getters.getThemeColor,
              },
              {
                text: company.email,
                style: [this.getPdfStyles().email, this.getPdfStyles().italics],
                alignment: "right",
                border: [false, false, false, false],
                color,
                fillColor: store.getters.getThemeColor,
              },
            ],
          ],
        },
      },
      this.myCustomMarginHeaderBottom(),
    ];
  },
  dateAndPersonalInfo(client, invoiceDate, invoiceNumber, type = "Invoice") {
    return {
      style: [this.getPdfStyles().totalsPane],
      table: {
        widths: ["40%", "*", "*", "40%"],
        body: [
          [
            {
              border: [false, false, false, true],
              fillColor: store.getters.getThemeColor,
              color,
              text: `${client.name}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false, false, false, true],
              fillColor: store.getters.getThemeColor,
              color,
              text: `${client.position || "N/A"}`,
            },
          ],
          [
            {
              border: [false, false, false, true],
              fillColor: "#dddddd",
              text: `Reference: ${client.client_reference}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false, false, false, true],
              fillColor: "#dddddd",
              text: `${client.institution || "N/A"}`,
            },
          ],
          [
            {
              border: [false, false, false, true],
              fillColor: "#dddddd",
              text: `${client.address}. ${client.city}, ${client.postal_code}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false, false, false, true],
              fillColor: "#dddddd",
              text: `Date: ${invoiceDate} \n ${type} #: ${invoiceNumber}`,
            },
          ],
        ],
      },
    };
  },
  itemsDetailsTable(selectedInvoice) {
    const items = selectedInvoice.items;

    const itemsTableBody = [this.myCustomMarginTop()];

    if (items.find((item) => item.item_type == "product")) {
      itemsTableBody.push(this.productsHeader());
      items.forEach((item) => {
        if (item.item_type == "product") {
          itemsTableBody.push(this.itemDetails(item));
        }
      });
    }

    if (items.find((item) => item.item_type == "service")) {
      itemsTableBody.push(this.servicesHeader());
      items.forEach((item) => {
        if (item.item_type == "service") {
          itemsTableBody.push(this.itemDetails(item));
        }
      });
    }

    if (items.find((item) => item.item_type == "transportation")) {
      itemsTableBody.push(this.transportsHeader());
      items.forEach((item) => {
        if (item.item_type == "transportation") {
          itemsTableBody.push(this.itemDetails(item));
        }
      });
    }

    return {
      style: [this.getPdfStyles().tableStyle, this.getPdfStyles().totalsPane],
      table: {
        widths: ["52%", "18%", "15%", "15%"],
        body: itemsTableBody,
      },
    };
  },
  tableTotalsPane(selectedInvoice, refNumber, company) {
    return {
      style: [this.getPdfStyles().tableStyle, this.getPdfStyles().totalsPane],
      table: {
        widths: ["40%", "*", 100, "40%"],
        body: [
          this.myCustomMarginTop(),
          [
            {
              border: [true, true, true, true],
              color,
              fillColor: store.getters.getThemeColor,
              text: `Banking Details`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [true, true, true, true],
              color,
              fillColor: store.getters.getThemeColor,
              text: `Computed Summary`,
            },
          ],
          [
            {
              border: [false, true, false, true],
              text: `Bank: ${company.bank}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },

            {
              border: [false, true, false, true],
              table: {
                widths: ["50%", "50%"],
                body: [
                  [
                    {
                      border: [false, false, false, false],
                      text: `Sub Total:`,
                    },
                    {
                      border: [false, false, false, false],
                      text: `${
                        store.getters.getCurrency
                      } ${helpersJs.formatMoney(selectedInvoice.subTotal)}`,
                      alignment: "right",
                    },
                  ],
                ],
              },
            },
          ],
          [
            {
              border: [false, true, false, true],
              text: `Account #: ${company.account_number}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false, true, false, true],
              table: {
                widths: ["50%", "50%"],
                body: [
                  [
                    {
                      border: [false, false, false, false],
                      text: `Total Vat:`,
                    },
                    {
                      border: [false, false, false, false],
                      text: `${
                        store.getters.getCurrency
                      }${helpersJs.formatMoney(selectedInvoice.totalTax)}`,
                      alignment: "right",
                    },
                  ],
                ],
              },
            },
          ],

          [
            {
              border: [false, true, false, true],
              text: `Type: ${company.account_type}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },

            {
              border: [false, true, false, true],
              table: {
                widths: ["50%", "50%"],
                body: [
                  [
                    {
                      border: [false, false, false, false],
                      text: `Discount:`,
                    },
                    {
                      border: [false, false, false, false],
                      text: `${
                        store.getters.getCurrency
                      } ${helpersJs.formatMoney(selectedInvoice.discount)}`,
                      alignment: "right",
                    },
                  ],
                ],
              },
            },
          ],
          [
            {
              border: [false, true, false, true],
              text: `Ref#: ${refNumber}`,
            },
            {
              border: [false],
              text: "",
            },
            {
              border: [false],
              text: "",
            },

            {
              border: [false, true, false, true],
              table: {
                widths: ["50%", "50%"],
                body: [
                  [
                    {
                      border: [false, false, false, false],
                      text: `Total:`,
                    },
                    {
                      border: [false, false, false, false],
                      text: `${
                        store.getters.getCurrency
                      } ${helpersJs.formatMoney(
                        selectedInvoice.totalInvoiceAmount
                      )}`,
                      alignment: "right",
                    },
                  ],
                ],
              },
            },
          ],
        ],
      },
    };
  },
  productsHeader() {
    return [
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.products}`,
        alignment: "left",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.price}`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.quantity}`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `Line Total`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
    ];
  },
  servicesHeader() {
    return [
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.services}`,
        alignment: "left",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.fee}`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        alignment: "right",
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.duration}`,
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `Line Total`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
    ];
  },
  transportsHeader() {
    return [
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.transportations}`,
        alignment: "left",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.transportationFee} per ${store.getters.getCompanyLabels.distance}`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `${store.getters.getCompanyLabels.distance}`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
      {
        border: [true, true, true, true],
        fillColor: store.getters.getThemeColor,
        color,
        text: `Line Total`,
        alignment: "right",
        style: [this.getPdfStyles().addressSection],
      },
    ];
  },
  itemDetails(item) {
    return [
      {
        border: [false, true, false, true],
        text: `${item.name}`,
      },
      {
        border: [false, true, false, true],
        alignment: "right",
        text: `${store.getters.getCurrency}${helpersJs.formatMoney(
          item.price
        )} \n ${item.increments ? "[" + item.increments + "]" : ""}`,
      },
      {
        border: [false, true, false, true],
        alignment: "right",
        text: `${item.quantity ? item.quantity : item.distance}`,
      },
      {
        border: [false, true, false, true],
        alignment: "right",
        text: `${store.getters.getCurrency}${helpersJs.formatMoney(
          item.lineTotal
        )}`,
      },
    ];
  },
  async getContent(
    company,
    description,
    selectedInvoice,
    selectedClient,
    invoiceDate
  ) {
    const content = [];
    const innerContent = [];

    innerContent.push(await this.companyHeader(description));

    if (selectedClient) {
      innerContent.push(
        this.dateAndPersonalInfo(
          selectedClient,
          invoiceDate,
          selectedInvoice.invoiceNumber,
          description
        )
      );
    }

    if (selectedInvoice.items && selectedInvoice.items.length > 0) {
      innerContent.push(this.itemsDetailsTable(selectedInvoice));
    }
    if (
      selectedInvoice.totalServices > 0 ||
      selectedInvoice.totalProducts > 0 ||
      selectedInvoice.totalTransports > 0
    ) {
      innerContent.push(
        this.tableTotalsPane(
          selectedInvoice,
          selectedInvoice.invoiceNumber,
          company
        )
      );
    }
    //if (selectedInvoice && selectedInvoice.description != description) {
    innerContent.push(this.commentLine(selectedInvoice.description));
    //}
    // if (store.getters.getUserCompany.stamp) {
    //   innerContent.push(this.companyStamp());
    // }
    // innerContent.push({
    //   text: `\n ${company.name}`,
    //   alignment: "center",
    // });
    innerContent.push({
      text: `\n created with BitsOffice [076 243 9441]`,
      alignment: "center",
    });
    content.push(innerContent);
    return content;
  },
  async getContentFrontPage(
    company,
    description,
    selectedInvoice,
    selectedClient,
    invoiceDate,
    logoBase64Url
  ) {
    const content = [];
    const innerContent = [];

    innerContent.push(
      await this.companyHeaderFrontPage(company, description, logoBase64Url)
    );

    if (selectedClient) {
      innerContent.push(
        this.dateAndPersonalInfo(
          selectedClient,
          invoiceDate,
          selectedInvoice.invoiceNumber,
          description
        )
      );
    }

    if (selectedInvoice.items && selectedInvoice.items.length > 0) {
      innerContent.push(this.itemsDetailsTable(selectedInvoice));
    }
    if (
      selectedInvoice.totalServices > 0 ||
      selectedInvoice.totalProducts > 0 ||
      selectedInvoice.totalTransports > 0
    ) {
      innerContent.push(
        this.tableTotalsPane(
          selectedInvoice,
          selectedInvoice.invoiceNumber,
          company
        )
      );
    }
    //if (selectedInvoice && selectedInvoice.description != description) {
    innerContent.push(this.commentLine(selectedInvoice.description));
    //}
    // if (store.getters.getUserCompany.stamp) {
    //   innerContent.push(this.companyStamp());
    // }
    // innerContent.push({
    //   text: `\n ${company.name}`,
    //   alignment: "center",
    // });
    innerContent.push({
      text: `\n Created @ https://baronitsolutions.co.za/free-invoices-and-quotes`,
      link: "https://baronitsolutions.co.za/free-invoices-and-quotes",
      color: "blue",
      alignment: "center",
    });

    innerContent.push({
      text: `\n Get BitsOffice [ +27 76 243 9441 | adubyte2@yahoo.com ]`,
      alignment: "center",
    });
    content.push(innerContent);
    return content;
  },
  commentLine(text) {
    return [
      {
        text: `\n ${text}`,
        alignment: "center",
      },
    ];
  },

  myCustomMarginHeaderBottom() {
    return [
      { text: "\n\n", border: [false, false, false, false] },
      { text: "\n\n", border: [false, false, false, false] },
    ];
  },
  myCustomMarginTop() {
    return [
      { text: "\n", border: [false, false, false, false] },
      { text: "\n", border: [false, false, false, false] },
      { text: "\n", border: [false, false, false, false] },
      { text: "\n", border: [false, false, false, false] },
    ];
  },
  getPdfStyles() {
    return {
      header: {
        fontSize: 28,
        bold: true,
        margin: [0, 0, 0, 0],
        color,
      },
      subHeader: {
        fontSize: 22,
        bold: true,
        margin: [0, 0, 0, 0],
        color,
      },
      addressSection: {
        fontSize: 14,
        margin: [0, 0, 0, 0],
        color,
        alignment: "left",
      },
      totalsPane: {
        fontSize: 14,
        margin: [0, 0, 0, 0],
        color: "#000",
        alignment: "left",
      },
      fillColumns: {
        margin: [0, 10, 0, 20],
      },
      tableStyle: {
        margin: [0, 0, 0, 0],
      },
      tableMainHeaderStyle: {
        fillColor: store.getters.getThemeColor,
        border: [false, false, false, false],
      },
      email: {
        fontSize: 12,
        color: "#2196F3",
        alignment: "center",
        decoration: "underline",
        margin: [0, 0, 0, 0],
      },
      quote: {
        italics: true,
      },
      small: {
        fontSize: 8,
      },
      paragraph: {
        fontSize: 12,
      },
      bold: {
        bold: true,
      },
      italics: {
        italics: true,
      },
      underline: {
        decoration: "underline",
      },
    };
  },
};
